import SvgIcon from '@/shared/ui/svg-icon'
import { Button, type ButtonProps } from 'antd'
import type { ButtonHTMLType, ButtonVariantType } from 'antd/es/button'
import { type ReactNode, forwardRef } from 'react'

export type UiButtonTone = 'neutral' | 'primary' | 'success' | 'warning' | 'danger'

const TONE_COLOR: Record<UiButtonTone, ButtonProps['color']> = {
  neutral: 'default',
  primary: 'primary',
  success: 'green',
  warning: 'orange',
  danger: 'danger',
}

export interface ButtonCustomProps extends Omit<ButtonProps, 'color'> {
  iconName?: string
  children?: ReactNode
  variant?: ButtonVariantType
  ghost?: boolean
  htmlType?: ButtonHTMLType
  tone?: UiButtonTone
  color?: ButtonProps['color']
}

// forwardRef để Tooltip và các component khác lấy được ref tới DOM button thực tế
const UiButton = forwardRef<HTMLButtonElement, ButtonCustomProps>(
  (
    {
      children,
      variant: variantProp,
      iconName = '',
      htmlType = 'button', // mặc định là 'button'
      danger = false,
      tone,
      color: colorOverride,
      icon,
      ...rest
    },
    ref
  ) => {
    const isLinkOrText = rest.type === 'link' || rest.type === 'text'
    const isPrimary = rest.type === 'primary' || tone === 'primary'
    // Mặc định cho UiButton là nút phụ (outlined, neutral) trừ khi là link/text/primary hoặc variant truyền tường minh
    const variant =
      variantProp ??
      (isLinkOrText ? (rest.type as ButtonVariantType) : isPrimary ? 'solid' : 'outlined')

    // Thứ tự ưu tiên: color truyền trực tiếp > tone > danger > type > mặc định 'default' (neutral/secondary)
    let resolvedColor: ButtonProps['color'] = colorOverride
    if (!resolvedColor) {
      if (tone) {
        resolvedColor = TONE_COLOR[tone]
      } else if (danger) {
        resolvedColor = 'danger'
      } else if (rest.type === 'primary') {
        resolvedColor = 'primary'
      } else {
        resolvedColor = 'default'
      }
    }

    // Xử lý icon: ưu tiên icon prop (JSX), sau đó đến iconName (SvgIcon)
    let resolvedIcon: ReactNode = undefined
    if (icon) {
      resolvedIcon = icon
    } else if (iconName) {
      resolvedIcon = <SvgIcon name={iconName} className={children ? 'mr-8' : ''} />
    }

    return (
      <Button
        ref={ref}
        variant={variant}
        color={resolvedColor}
        danger={danger || tone === 'danger' || resolvedColor === 'danger'}
        htmlType={htmlType}
        icon={resolvedIcon}
        {...rest}
      >
        {children ? (
          <div className="d-flex-center">
            <span>{children}</span>
          </div>
        ) : null}
      </Button>
    )
  }
)

UiButton.displayName = 'UiButton'

export default UiButton
export * from './UiActionButton'
